import { CURRENCY_SYMBOLS } from '@wagenow-ewa/common/dist/src/constants/currencies'
import { Button, Form } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { change, Field, InjectedFormProps, reduxForm } from 'redux-form'
import newWithdrawalImage from '../../assets/images/new-withdrawal.png'
import NumberInputField from '../../atoms/form/NumberInputField'
import WeekendNotification from '../../atoms/WeekendNotification'
import { RootState } from '../../redux'
import { WithdrawalMultiplier } from '../../utils/constants'
import { FORM } from '../../utils/enums'
import { formatAsCurrency } from '../../utils/helpers'
import { shouldBeRounded } from '../../utils/numberUtils'
import validateCreateWithdrawalForm from './validateCreateWithdrawalForm'

interface ICreateWithdrawalForm {
	handleCreateWithdrawalDrawerClose: () => void
	createWithdrawalFormSubmitSuccess: boolean
}

export interface ICreateWithdrawalFormValues {
	amount: number
	availableEarnings: number
}

const CreateWithdrawalForm: FC<InjectedFormProps<ICreateWithdrawalFormValues, ICreateWithdrawalForm> & ICreateWithdrawalForm> = ({
	handleCreateWithdrawalDrawerClose,
	createWithdrawalFormSubmitSuccess,
	initialValues,
	handleSubmit,
	submitting
}) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const form = useSelector((state: RootState) => state.form)
	const countryCode = useSelector((state: RootState) => state.currentUser.detail.record?.company?.countryCode)
	const currency = countryCode && countryCode.toLowerCase() === 'gb' ? 'GBP' : 'EUR'
	const value = form[FORM.WITHDRAWAL_CREATE]?.values?.amount || 0
	const roundedAmountData = shouldBeRounded(value, WithdrawalMultiplier, initialValues.availableEarnings)

	const withdrawalValues = [10, 20, 30, 50]

	const handleWithdrawalValueClick = (amount: number) => {
		dispatch(change(FORM.WITHDRAWAL_CREATE, 'amount', amount))
	}

	function renderRequestButton() {
		if (roundedAmountData.shouldRound) {
			return (
				<Button
					type={'primary'}
					shape={'round'}
					block={true}
					htmlType={'button'}
					className={'data-hj-allow'}
					onClick={(event) => {
						event.preventDefault()
						dispatch(change(FORM.WITHDRAWAL_CREATE, 'amount', roundedAmountData.roundTo))
						return false
					}}
				>
					{t('loc:general|Change to')} {formatAsCurrency(roundedAmountData.roundTo, countryCode)}
				</Button>
			)
		}
		return (
			<Button
				disabled={roundedAmountData.isEmpty}
				className={'data-hj-allow'}
				type={'primary'}
				shape={'round'}
				block={true}
				htmlType={'submit'}
				loading={submitting}
			>
				{t('loc:employee|Send request')}
			</Button>
		)
	}

	return createWithdrawalFormSubmitSuccess ? (
		<div className={'max-w-[21rem] mx-auto'}>
			<img src={newWithdrawalImage} alt={''} className={'mx-auto mb-3'} style={{ width: '240px' }} />
			<h4 className={'text-center text-36 font-700 leading-sm mb-3'}>
				{t('loc:employee|Your request')}
				<br />
				{`${t('loc:employee|is on its way')}!`}
			</h4>
			<p className={'text-center text-15 font-400 mb-6'}>
				{`${t('loc:employee|A withdrawal request has been sent')},`}
				<br />
				{`${t('loc:employee|we will let you know once it’s approved')}.`}
			</p>
			<Button type={'primary'} shape={'round'} block={true} onClick={handleCreateWithdrawalDrawerClose}>
				{t('loc:general|Okay')}
			</Button>
		</div>
	) : (
		<Form noValidate={true} onSubmitCapture={handleSubmit} className={'flex flex-col items-center create-withdrawal-form'}>
			<h4 className={'text-22 lg:text-56 font-700 mb-6 lg:mb-5'}>
				<span className={'inline lg:block'}>{t('loc:employee|How much money')} </span>
				<span className={'inline lg:block'}>{t('loc:employee|do you want to withdraw')}?</span>
			</h4>
			<div className={'max-w-[25rem]'}>
				<div className={'inline-flex items-center gap-1 text-gray-500 mb-6'}>
					<span className={'text-15 lg:text-17 font-400'}>{`${t('loc:employee|Your available Balance')}:`}</span>
					<span className={'text-17 lg:text-20 font-600 p-0 border-none data-hj-allow'}>{formatAsCurrency(initialValues.availableEarnings, countryCode)}</span>
				</div>
			</div>
			<div className={'max-w-[25rem]'}>
				<Field
					name={'amount'}
					component={NumberInputField}
					inputClassName={'data-hj-allow'}
					precision={0}
					placeholder={t('loc:general|Amount')}
					className={'mx-8 mb-3 data-hj-allow'}
				/>
				<div className={'max-w-[25rem] flex justify-center gap-2'}>
					{withdrawalValues.map((amount) => {
						const isDisabled = amount > (initialValues.availableEarnings || 0)
						return (
							<Button
								key={amount}
								type={'default'}
								size={'small'}
								className={'data-hj-allow'}
								disabled={isDisabled}
								onClick={() => !isDisabled && handleWithdrawalValueClick(amount)}
								style={{
									backgroundColor: '#ECE6F5',
									color: '#664A85',
									border: 'none',
									borderRadius: '10px',
									opacity: isDisabled ? 0.3 : 1,
									cursor: isDisabled ? 'not-allowed' : 'pointer'
								}}
							>
								{amount} {CURRENCY_SYMBOLS[currency]}
							</Button>
						)
					})}
				</div>
				<div className={'mt-6 w-full'}>
					<WeekendNotification />
				</div>
				<div className={'inline-flex items-center gap-1 text-gray-500 mt-3'}>{renderRequestButton()}</div>
			</div>
		</Form>
	)
}

export default reduxForm<ICreateWithdrawalFormValues, ICreateWithdrawalForm>({
	form: FORM.WITHDRAWAL_CREATE,
	validate: validateCreateWithdrawalForm,
	touchOnChange: true,
	destroyOnUnmount: true,
	forceUnregisterOnUnmount: true
})(CreateWithdrawalForm)
